import { DynamicTexture as BABYLON_DynamicTexture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/dynamicTexture.js';
import { ViewerMaterial } from './material/ViewerMaterial.js';
import { Mesh as BABYLON_Mesh } from '@inter-ikea-betrakta-babylonjs/core/Meshes/mesh.js';
import { VertexData as BABYLON_VertexData } from '@inter-ikea-betrakta-babylonjs/core/Meshes/mesh.vertexData.js';
import { Quaternion as BABYLON_Quaternion } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Engine as BABYLON_Engine } from '@inter-ikea-betrakta-babylonjs/core/Engines/engine.js';
import { IColor } from '../api.js';
import { RENDER_LAYER_UI, RenderLayerMask } from './RenderLayer.js';
function hex_from_color(color) {
    const saturated_color = IColor.saturate(color);
    let r = Math.floor(saturated_color.r * 255).toString(16);
    r = r.length === 1 ? `0${r}` : r;
    let g = Math.floor(saturated_color.g * 255).toString(16);
    g = g.length === 1 ? `0${g}` : g;
    let b = Math.floor(saturated_color.b * 255).toString(16);
    b = b.length === 1 ? `0${b}` : b;
    return `#${r.length === 1 ? '0' : ''}${r}${g.length === 1 ? '0' : ''}${g}${b.length === 1 ? '0' : ''}${b}`;
}
export class TextField {
    constructor(_viewer, _font_size, _max_length) {
        this._viewer = _viewer;
        this._font_size = _font_size;
        this._max_length = _max_length;
        this._text = '';
        this._render_layer_mask = new RenderLayerMask([RENDER_LAYER_UI]);
        this._width_height_ratio = 0.62;
        this._worldspace_height = 0.1;
        this.background_color = null;
        this.stroke_text = true;
        // With our monospace font, width is a radio of height, we add 20 pixels of extra padding (10+10)
        this._texture_width = 2048;
        this._texture = new BABYLON_DynamicTexture('TextFieldDynamicText', { width: Math.floor(this._font_size * this._max_length * this._width_height_ratio), height: this._font_size }, this._viewer.scene, true);
        this._material = new ViewerMaterial('TextFieldMaterial', this._viewer.scene, this._viewer);
        this._material.albedoTexture = this._texture;
        this._material.opacityTexture = this._texture;
        this._material.unlit = true;
        this._plane = new BABYLON_Mesh('TextFieldMesh', this._viewer.scene);
        this._plane.renderingGroupId = 3; // Same as lines (on top)
        this._plane.onAfterWorldMatrixUpdateObservable.add(() => {
            this._viewer.mark_scene_dirty();
            this._viewer.mark_world_bounds_dirty();
        });
        this._material.depthFunction = BABYLON_Engine.ALWAYS;
        this._vertex_data = new BABYLON_VertexData();
        this._plane.material = this._material;
        this._index_buffer = new Uint16Array(6);
        this._index_buffer[0] = 0;
        this._index_buffer[1] = 2;
        this._index_buffer[2] = 1;
        this._index_buffer[3] = 0;
        this._index_buffer[4] = 3;
        this._index_buffer[5] = 2;
        // There are 4 verts with 3 floats per position and 2 floats per uv
        this._position_buffer = new Float32Array(4 * 3);
        this._uv_buffer = new Float32Array(4 * 2);
        this._vertex_data.positions = this._position_buffer;
        this._vertex_data.uvs = this._uv_buffer;
        this._vertex_data.indices = this._index_buffer;
        this.update_mesh_buffers(this._worldspace_height);
        this._viewer.asset_manager.text_fields.add(this);
    }
    get_meshes() {
        return [this._plane];
    }
    get_width() {
        return this._worldspace_height * this._width_height_ratio * this._text.length;
    }
    set_position(x, y, z) {
        this._plane.position.set(x, y, z);
    }
    set_rotation(x, y, z) {
        if (!this._plane.rotationQuaternion) {
            this._plane.rotationQuaternion = new BABYLON_Quaternion();
        }
        BABYLON_Quaternion.FromEulerAnglesToRef(x, y, z, this._plane.rotationQuaternion);
    }
    update_render_layer_mask() {
        this._plane.layerMask = this._render_layer_mask.bitmap;
        this._viewer.mark_scene_dirty();
    }
    set_render_layer_mask(mask) {
        if (this._render_layer_mask.bitmap === mask.bitmap)
            return;
        this._render_layer_mask = Object.assign({}, mask);
        this.update_render_layer_mask();
    }
    get_render_layer_mask() {
        return this._render_layer_mask;
    }
    update_mesh_buffers(height) {
        const half_height = height * 0.5;
        const half_width = height * this._width_height_ratio * this._text.length * 0.5;
        // 1 _______2
        //  |      /|
        //  |   /   |
        //  |/______|
        // 0         3
        // 0
        // TODO: Fix better UVs
        this._position_buffer[0] = -half_width;
        this._position_buffer[1] = 0;
        this._position_buffer[2] = -half_height;
        this._uv_buffer[0] = 0;
        this._uv_buffer[1] = 1;
        // 1
        this._position_buffer[3] = -half_width;
        this._position_buffer[4] = 0;
        this._position_buffer[5] = half_height;
        this._uv_buffer[2] = 0;
        this._uv_buffer[3] = 0;
        // 2
        this._position_buffer[6] = half_width;
        this._position_buffer[7] = 0;
        this._position_buffer[8] = half_height;
        this._uv_buffer[4] = this._text.length / this._max_length;
        this._uv_buffer[5] = 0;
        // 3
        this._position_buffer[9] = half_width;
        this._position_buffer[10] = 0;
        this._position_buffer[11] = -half_height;
        this._uv_buffer[6] = this._text.length / this._max_length;
        this._uv_buffer[7] = 1;
        this._vertex_data.applyToMesh(this._plane);
    }
    set_text(text) {
        if (text.length > this._max_length) {
            console.error(`text is more than ${this._max_length} characters. this requires a larger buffer`);
        }
        this._text = text;
        const size = this._texture.getSize();
        const texture_context = this._texture.getContext();
        texture_context.clearRect(0, 0, size.width, size.height);
        if (this.background_color) {
            texture_context.fillStyle = hex_from_color(this.background_color);
            texture_context.fillRect(0, 0, size.width, size.height);
        }
        texture_context.font = `bold ${this._font_size}px arial`;
        texture_context.fillStyle = '#FFFFFF';
        texture_context.fillText(text, 0, this._font_size * 0.75);
        if (this.stroke_text) {
            texture_context.strokeStyle = '#000';
            texture_context.strokeText(text, 0, this._font_size * 0.75);
        }
        this._texture.update();
        this.update_mesh_buffers(this._worldspace_height);
        this._viewer.mark_scene_dirty();
    }
    dispose() {
        this._material.dispose();
        this._texture.dispose();
        this._plane.dispose();
        this._viewer.asset_manager.text_fields.delete(this);
    }
}
//# sourceMappingURL=TextField.js.map